import type { Condition } from '@/config/utils'
import { apiCall } from './axiosInstance'
import type { NotificationChannelType } from './subscriberGroupsApi'
export type { NotificationChannelType } from './subscriberGroupsApi'

// Paginated response wrapper
export type PaginatedResponse<T, S = Record<string, number>> = {
	count: number
	data: T[]
	summary?: S
}

// Pagination params for API calls
export type PaginationParams = {
	page?: number
	per_page?: number
}

export type SessionRuleType = 'inactive' | 'no_data' | 'signal'
export type SessionRuleStatus = 'started' | 'stopped'
export type SessionRuleSeverity = 'critical' | 'warning' | 'info'

// Subscriber group assignment for notification rules
export type AssignSubscriberGroup = {
	subscriber_group_id: number
	name: string
	activation_template?: string
	deactivation_template?: string
	timezone?: string
}

// Notification rule schema (embedded in session rule)
export type NotificationRuleSchema = {
	notify_on_deactivation: boolean
	subscriber_group_ids: number[]
}

export type SessionRuleSubscriberGroup = {
	id: number
	name: string
	channel_type: NotificationChannelType
}

// Opaque to the backend: it stores whatever is sent and replaces the whole object on update.
export type SessionRuleFrontendSettings = {
	icon?: string
	[key: string]: unknown
}

// Full session rule response from API (matches SessionRuleResponse in OpenAPI spec)
export type SessionRule = {
	id: string // UUID
	name: string
	tenant_id: string
	stream_id: string
	session_type: SessionRuleType
	status: SessionRuleStatus
	severity: SessionRuleSeverity
	notification_enabled: boolean
	activation_threshold_secs: number
	deactivation_threshold_secs: number
	stale_timeout_mins?: number
	condition?: Condition | null
	device_filter?: string[] | null
	metadata_filters?: Record<string, string[]> | null
	deepdive_dashboard_id?: string | null
	frontend_settings?: SessionRuleFrontendSettings
	notification_rule?: NotificationRuleSchema | null
	last_processed_timestamps?: Record<string, number> // shard_id -> epoch_ms (keys are stringified in JSON)
	max_input_stream_timestamp?: number | null
	last_error?: string | null
	last_error_timestamp?: string | number | null
	// Top-level in list responses; mirrors notification_rule fields
	subscriber_group_ids?: number[]
	subscriber_groups?: SessionRuleSubscriberGroup[]
	notify_on_deactivation?: boolean
}

// Create session rule request
export type CreateSessionRuleRequest = {
	name: string
	stream_id: string
	session_type: SessionRuleType
	severity: SessionRuleSeverity
	notification_enabled: boolean
	activation_threshold_secs?: number
	deactivation_threshold_secs?: number
	stale_timeout_mins?: number
	condition?: Condition | null
	device_filter?: string[] | null
	metadata_filters?: Record<string, string[]> | null
	deepdive_dashboard_id?: string
	frontend_settings?: SessionRuleFrontendSettings
	notification_rule?: NotificationRuleSchema
}

// Update session rule request (session config only - notification has separate endpoint)
export type UpdateSessionRuleRequest = {
	name: string
	activation_threshold_secs: number
	deactivation_threshold_secs: number
	stale_timeout_mins?: number
	severity: SessionRuleSeverity
	condition?: Condition | null
	device_filter?: string[] | null
	metadata_filters?: Record<string, string[]> | null
	deepdive_dashboard_id?: string
	frontend_settings?: SessionRuleFrontendSettings
}

// Session rules summary type
export type SessionRulesSummary = {
	no_data?: number
	signal?: number
	inactive?: number
}

// Session rule filters for API
export type SessionRuleFilters = {
	name?: string
	session_type?: SessionRuleType
	severity?: SessionRuleSeverity
	status?: SessionRuleStatus
	stream_id?: string
}

export type NotificationHistoryFilters = {
	device_id?: string
	rule_name?: string
	subscriber_group_id?: number
	start_time?: number // epoch millis
	end_time?: number // epoch millis
}

export type NotificationHistoryItem = {
	id: number
	tenant_id: string
	session_id: number
	session_rule_id: string
	rule_name: string
	device_id: string
	notification_rule_id: number
	subscriber_group_id: number
	subscriber_group_name: string
	event_type: 'activation' | 'deactivation'
	contents: string
	sent_at: string
}

export type NotificationFailureFilters = {
	device_id?: string
	rule_name?: string
	subscriber_group_id?: number
	resolved?: boolean
	start_time?: number // epoch millis
	end_time?: number // epoch millis
}

export type TestNotificationResult = {
	channel: string
	group: string
	notification_type: string
	status: 'success' | 'failed'
	error?: string
}

export type NotificationFailureItem = {
	id: number
	tenant_id: string
	session_id: number
	session_rule_id: string
	rule_name: string
	device_id: string
	notification_rule_id: number
	subscriber_group_id: number
	subscriber_group_name: string
	event_type: 'activation' | 'deactivation'
	contents: string
	failure_reason: string
	attempt_number: number
	resolved: boolean
	created_at: string
}

// Helper to build query string from pagination params
const buildPaginationQuery = (params?: PaginationParams): string => {
	const queryParams = new URLSearchParams()
	if (params?.page !== undefined) {
		queryParams.set('page', String(params.page))
	}
	if (params?.per_page !== undefined) {
		queryParams.set('per_page', String(params.per_page))
	}
	const queryString = queryParams.toString()
	return queryString ? `?${queryString}` : ''
}

// Helper to build filter body from filters object
const buildFilterBody = <T extends Record<string, unknown>>(filters?: T, strictKeys: (keyof T)[] = []): Partial<T> => {
	if (!filters) {
		return {}
	}
	const filterBody: Partial<T> = {}
	for (const key of Object.keys(filters) as (keyof T)[]) {
		const value = filters[key]
		if (strictKeys.includes(key)) {
			if (value !== undefined) {
				filterBody[key] = value
			}
		} else {
			if (value) {
				filterBody[key] = value
			}
		}
	}
	return filterBody
}

export const sessionsApi = {
	/**
	 * Fetches all session rules (paginated)
	 * Required permission: session-rule:read
	 */
	getSessionRules: async (
		params?: PaginationParams,
		filters?: SessionRuleFilters,
	): Promise<PaginatedResponse<SessionRule, SessionRulesSummary>> => {
		// Build query string for pagination
		const queryString = buildPaginationQuery(params)

		// Build request body for filters only
		const filterBody = buildFilterBody(filters)

		return await apiCall(`/v3/session-rules${queryString}`, {
			method: 'POST',
			data: filterBody,
		})
	},

	/**
	 * Fetches a single session rule by ID
	 * Required permission: session-rule:read
	 */
	getSessionRule: async (id: string): Promise<SessionRule> => {
		return await apiCall(`/v3/session-rules/${encodeURIComponent(id)}`)
	},

	/**
	 * Creates a new session rule
	 * Required permission: session-rule:update
	 */
	createSessionRule: async (rule: CreateSessionRuleRequest): Promise<SessionRule> => {
		return await apiCall('/v3/session-rules/add', {
			method: 'POST',
			data: rule,
		})
	},

	/**
	 * Updates an existing session rule
	 * Required permission: session-rule:update
	 */
	updateSessionRule: async (id: string, rule: UpdateSessionRuleRequest): Promise<{ message: string }> => {
		return await apiCall(`/v3/session-rules/${encodeURIComponent(id)}`, {
			method: 'PUT',
			data: rule,
		})
	},

	/**
	 * Deletes a session rule (must be stopped first)
	 * Required permission: session-rule:update
	 */
	deleteSessionRule: async (id: string): Promise<{ message: string }> => {
		return await apiCall(`/v3/session-rules/${encodeURIComponent(id)}`, {
			method: 'DELETE',
		})
	},

	/**
	 * Starts a session rule
	 * Required permission: session-rule:update
	 */
	startSessionRule: async (id: string): Promise<{ message: string }> => {
		return await apiCall(`/v3/session-rules/${encodeURIComponent(id)}/start`, {
			method: 'PUT',
		})
	},

	/**
	 * Stops a session rule
	 * Required permission: session-rule:update
	 */
	stopSessionRule: async (id: string): Promise<{ message: string }> => {
		return await apiCall(`/v3/session-rules/${encodeURIComponent(id)}/stop`, {
			method: 'PUT',
		})
	},

	/**
	 * Updates notification rule for a session rule (upsert - creates if doesn't exist)
	 * Required permission: session-rule:update
	 */
	updateNotificationRule: async (id: string, rule: NotificationRuleSchema): Promise<{ message: string }> => {
		return await apiCall(`/v3/session-rules/${encodeURIComponent(id)}/notification`, {
			method: 'PUT',
			data: rule,
		})
	},

	/**
	 * Enables notifications for a session rule
	 * Required permission: session-rule:update
	 */
	enableNotifications: async (id: string): Promise<{ message: string }> => {
		return await apiCall(`/v3/session-rules/${encodeURIComponent(id)}/notification/enable`, {
			method: 'PUT',
		})
	},

	/**
	 * Disables notifications for a session rule
	 * Required permission: session-rule:update
	 */
	disableNotifications: async (id: string): Promise<{ message: string }> => {
		return await apiCall(`/v3/session-rules/${encodeURIComponent(id)}/notification/disable`, {
			method: 'PUT',
		})
	},

	/**
	 * Tests a notification rule configuration (does not persist)
	 * Required permission: session-rule:update
	 */
	testNotificationRule: async (rule: NotificationRuleSchema): Promise<TestNotificationResult[]> => {
		return await apiCall('/v3/session-rules/test', {
			method: 'PUT',
			data: rule,
		})
	},

	/**
	 * Fetches notification history (paginated with filters)
	 * Required permission: session-rule:read
	 */
	getNotificationHistory: async (
		params?: PaginationParams,
		filters?: NotificationHistoryFilters,
	): Promise<PaginatedResponse<NotificationHistoryItem>> => {
		const queryString = buildPaginationQuery(params)

		// Build request body for filters
		const filterBody = buildFilterBody(filters, ['subscriber_group_id'])

		return await apiCall(`/v3/notification-history${queryString}`, {
			method: 'POST',
			data: filterBody,
		})
	},

	/**
	 * Get a single notification history entry by ID
	 * Required permission: session-rule:read
	 */
	getNotificationHistoryEntry: async (id: number): Promise<NotificationHistoryItem> => {
		return await apiCall(`/v3/notification-history/${id}`)
	},

	/**
	 * Fetches notification failures (paginated with filters)
	 * Required permission: session-rule:read
	 */
	getNotificationFailures: async (
		params?: PaginationParams,
		filters?: NotificationFailureFilters,
		signal?: AbortSignal,
	): Promise<PaginatedResponse<NotificationFailureItem>> => {
		const queryString = buildPaginationQuery(params)

		// Build request body for filters
		const filterBody = buildFilterBody(filters, ['subscriber_group_id', 'resolved'])

		return await apiCall(`/v3/notification-failures${queryString}`, {
			method: 'POST',
			data: filterBody,
			signal,
		})
	},

	/**
	 * Get a single notification failure by ID
	 * Required permission: session-rule:read
	 */
	getNotificationFailure: async (id: number): Promise<NotificationFailureItem> => {
		return await apiCall(`/v3/notification-failures/${id}`)
	},

	/**
	 * Fetches available timezones
	 * Required permission: notification:read
	 */
	getTimezones: async (): Promise<string[]> => {
		const response = await apiCall<{ timezones: string[] }>('/v3/timezones')
		return response.timezones
	},
}
